import React, { useEffect, useState } from 'react';
import { config } from '../../config';
import styles from './InstallPrompt.module.css';

// Chrome fires this instead of showing its own install banner; sites are
// expected to present their own install UI and call prompt() from it.
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_AT_KEY = 'pwa-install-dismissed-at';
const DISMISS_DAYS = 14;

const isRecentlyDismissed = (): boolean => {
  try {
    const dismissedAt = localStorage.getItem(DISMISSED_AT_KEY);
    if (!dismissedAt) return false;
    const elapsedMs = Date.now() - Number(dismissedAt);
    return elapsedMs < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

const rememberDismissal = () => {
  try {
    localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
  } catch {
    // localStorage unavailable (private mode) - the banner just reappears next visit
  }
};

// iPadOS 13+ masquerades as desktop Safari ("MacIntel"), but real Macs have no touch
const isIos = (): boolean =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
  (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  // Safari-specific flag for home-screen apps
  (window.navigator as any).standalone === true;

// iOS Share glyph (square with up arrow) - points users at the right button
const IosShareIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ verticalAlign: 'text-bottom' }}>
    <path d="M12 3v12" />
    <path d="M8 6l4-4 4 4" />
    <path d="M5 10h-1v11h16V10h-1" />
  </svg>
);

export function InstallPrompt() {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosInstructions, setShowIosInstructions] = useState(false);

  useEffect(() => {
    if (!config.pwaEnabled) return;
    // Already running as an installed app
    if (isStandalone()) return;

    // iOS has no beforeinstallprompt - show add-to-home-screen instructions instead
    if (isIos()) {
      if (!isRecentlyDismissed()) {
        setShowIosInstructions(true);
      }
      return;
    }

    const onBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      if (isRecentlyDismissed()) return;
      setPromptEvent(e as BeforeInstallPromptEvent);
    };
    const onAppInstalled = () => setPromptEvent(null);

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  if (showIosInstructions) {
    const dismissIos = () => {
      rememberDismissal();
      setShowIosInstructions(false);
    };
    return (
      <div className={styles.overlay}>
        <div className={styles.banner}>
          <p className={styles.text}>
            Zainstaluj aplikację {config.siteName}: kliknij <IosShareIcon /> Udostępnij,
            a następnie „Do ekranu początkowego”.
          </p>
          <div className={styles.actions}>
            <button className={styles.dismissButton} onClick={dismissIos} aria-label="Zamknij">
              ✕
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!promptEvent) return null;

  const install = async () => {
    const event = promptEvent;
    setPromptEvent(null);
    await event.prompt();
    const { outcome } = await event.userChoice;
    if (outcome === 'dismissed') {
      rememberDismissal();
    }
  };

  const dismiss = () => {
    rememberDismissal();
    setPromptEvent(null);
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.banner}>
        <p className={styles.text}>
          Zainstaluj aplikację {config.siteName} na swoim urządzeniu.
        </p>
        <div className={styles.actions}>
          <button className={styles.installButton} onClick={install}>
            Zainstaluj
          </button>
          <button className={styles.dismissButton} onClick={dismiss} aria-label="Zamknij">
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
